import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'
import type { UseLoadMorePayload } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

const watchEntityKeys = ['webhooks']

export type WebhooksFilters = {
  owner_id?: number,
  owner_type?: string,
  string?: string,
}

interface UseWebhooksOptions {
  entityKey?: string,
  filters?: WebhooksFilters,
  performHttpRequests?: boolean,
}

function useWebhooks(options: UseWebhooksOptions = {}) {
  const { entityKey, filters } = options

  const {
    owner_id: filterOwnerId,
    owner_type: filterOwnerType,
    string,
  } = filters || {}

  const {
    updatedEntities: { webhooks: webhooksUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const webhooks = useSelector(reduxState => reduxState.entities.webhooks)

  const filteredWebhooks = useMemo(() => {
    const filtered = Object.values(webhooks).filter((webhook) => {
      const {
        id,
        owner_id,
        owner_type,
        title,
      } = webhook

      const matchOwnerId = matchFilterNumber(owner_id, filterOwnerId)
      const matchOwnerType = filterOwnerType ? owner_type === filterOwnerType : true
      const matchString = matchFilterString(title, string) || matchFilterString(id.toString(), string)

      return matchOwnerId && matchOwnerType && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [webhooksUpdatedAt, JSON.stringify(filters)])

  const filteredWebhooksCount = filteredWebhooks.length
  const hasFilteredWebhooks = !!filteredWebhooksCount

  const loadMorePayload: UseLoadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredWebhooksCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingWebhooks } = useReduxAction(
    'webhooks',
    'loadWebhooks',
    {
      entityKey,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredWebhooks,
    filteredWebhooksCount,
    hasFilteredWebhooks,
    loading: loadingWebhooks,
  }
}

export default useWebhooks
